import { Button, Drawer, Typography } from '@navi/web-ui/lib/primitives';
import React, { useState } from 'react';
import styles from './index.module.scss';
import FeeWaiverHistoryItem from './FeeWaiverHistoryItem';
import { FEE_WAIVER_LOADING_LIST, FeeWaiverHistory, GroupedWaiverHistory } from './interfaces';
import { groupByMonthAndYear } from './utils';
import { getFeeWaiveHistory } from '../../actions/EmiScheduleActions';
import { useParams } from 'react-router-dom';
import SuspenseLoader from '@cp/src/components/SkeletonLoader/SuspenseLoader';
import LineLoader from '@cp/src/components/SkeletonLoader/LineLoader';
import FeeWaiverHistoryLoadingItem from './FeeWaiverHistoryLoadingItem';
import NoFeeWaiveHistoryIcon from '@cp/src/assets/icons/NoFeeWaiveHistoryIcon';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { FEE_WAIVER_CLICKSTREAM } from '@cp/src/service/clickStream.constant';

const FeeWaiverHistoryV2 = () => {
  const { loanId = '' } = useParams();
  const [showFeeWaiverDrawer, setShowFeeWaiverDrawer] = useState(false);
  const [feeWaiverHistory, setFeeWaiverHistory] = React.useState<FeeWaiverHistory[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const feeWaiverHistoryList: GroupedWaiverHistory = React.useMemo(
    () => groupByMonthAndYear(feeWaiverHistory),
    [feeWaiverHistory]
  );

  const handleDrawerToggle = () => {
    addClickstreamEvent(FEE_WAIVER_CLICKSTREAM.LH_WAIVE_HISTORY_BUTTON_CLICKED, {
      loanAccountNumber: loanId
    });
    setShowFeeWaiverDrawer(!showFeeWaiverDrawer);
  };

  const fetchFeeWaiverHistory = async () => {
    setLoading(true);
    try {
      const response = await getFeeWaiveHistory(loanId);
      addClickstreamEvent(FEE_WAIVER_CLICKSTREAM.LH_WAIVE_HISTORY_LOADED, {
        loanAccountNumber: loanId
      });
      if (response) {
        setFeeWaiverHistory(response);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (showFeeWaiverDrawer) {
      fetchFeeWaiverHistory();
    }
    return () => {
      setLoading(false);
      setFeeWaiverHistory([]);
    };
  }, [showFeeWaiverDrawer]);

  return (
    <div>
      <Button variant="text" onClick={handleDrawerToggle}>
        Fee Waive / Hold History
      </Button>
      <Drawer
        open={showFeeWaiverDrawer}
        onClose={handleDrawerToggle}
        headerText="Fee waive / hold history"
        headerContainerClasses={styles.borderBottom}
        usePadding={false}
        contentClasses="overflow-auto"
      >
        {feeWaiverHistory?.length === 0 && !loading ? (
          <div className="flex items-center justify-center h-full">
            <NoFeeWaiveHistoryIcon />
          </div>
        ) : (
          Object.entries(loading ? FEE_WAIVER_LOADING_LIST : feeWaiverHistoryList).map(
            ([monthYear, feeWaiverItem]) => (
              <div key={monthYear}>
                <Typography
                  variant="h5"
                  className="bg-[var(--grayscale-background-secondary)] px-4 py-3"
                >
                  <SuspenseLoader
                    loading={loading}
                    fallBack={
                      <LineLoader
                        numberOfLines={1}
                        childrenType="H5"
                        dimenshionUnit="px"
                        lineWidth={100}
                      />
                    }
                  >
                    {monthYear}
                  </SuspenseLoader>
                </Typography>
                <div className="px-4">
                  {feeWaiverItem.map((item: FeeWaiverHistory, index: number) => (
                    <SuspenseLoader
                      key={item?.createdAt}
                      loading={loading}
                      fallBack={
                        <FeeWaiverHistoryLoadingItem
                          key={item.createdAt}
                          isLastItem={index === feeWaiverItem.length - 1}
                          feeWaiverItem={item}
                        />
                      }
                    >
                      <FeeWaiverHistoryItem
                        key={item?.createdAt}
                        feeWaiverItem={item}
                        isLastItem={index === feeWaiverItem.length - 1}
                      />
                    </SuspenseLoader>
                  ))}
                </div>
              </div>
            )
          )
        )}
      </Drawer>
    </div>
  );
};

export default FeeWaiverHistoryV2;
